import { useState } from "react";
import { Mail, Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/contexts/LangContext";

const schema = z.object({
  email: z.string().trim().email().max(320),
  name: z.string().trim().max(120).optional().or(z.literal("")),
  interest: z.string().max(200).optional(),
});

type Variant = "light" | "dark";

interface Props {
  variant?: Variant;
  source?: string;
  compact?: boolean;
}

const INTERESTS: Array<{ v: string; en: string; bn: string }> = [
  { v: "doctor", en: "Solo doctor", bn: "একক ডাক্তার" },
  { v: "multi-chamber", en: "Doctor with multiple chambers", bn: "একাধিক চেম্বারের ডাক্তার" },
  { v: "student", en: "Medical student / intern", bn: "মেডিকেল ছাত্র / ইন্টার্ন" },
  { v: "other", en: "Just curious", bn: "শুধু জানতে চাই" },
];

export default function NewsletterSignup({
  variant = "light",
  source = "footer_newsletter",
  compact = false,
}: Props) {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [interest, setInterest] = useState<string>("doctor");
  const [state, setState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const dark = variant === "dark";
  const displayFont =
    lang === "bn"
      ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
      : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const parsed = schema.safeParse({ email, name, interest });
    if (!parsed.success) {
      setState("error");
      setErrorMsg(
        t(
          "Please enter a valid email address.",
          "একটি বৈধ ইমেইল ঠিকানা দিন।",
        ),
      );
      return;
    }

    setState("loading");
    try {
      const { error } = await supabase.from("leads").insert({
        email: parsed.data.email,
        name: parsed.data.name || null,
        interest,
        source,
        page_path:
          typeof window !== "undefined" ? window.location.pathname : null,
        lang,
        consent_marketing: true,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      });

      if (error) {
        // 23505 = unique_violation → treat as already-subscribed success
        if (error.code === "23505") {
          setState("success");
          return;
        }
        throw error;
      }
      setState("success");
      setEmail("");
      setName("");
    } catch (err) {
      console.error("Newsletter signup failed:", err);
      setState("error");
      setErrorMsg(
        t(
          "Something went wrong. Please try again.",
          "কিছু ভুল হয়েছে। আবার চেষ্টা করুন।",
        ),
      );
    }
  };

  if (state === "success") {
    return (
      <div
        className={`rounded-2xl border p-6 flex items-start gap-3 ${
          dark
            ? "border-white/15 bg-white/[0.06] text-white"
            : "border-[#bbf7d0] bg-[#f0fdf4] text-[#065f46]"
        }`}
      >
        <CheckCircle2 className={`w-5 h-5 mt-0.5 ${dark ? "text-[#5eead4]" : "text-[#059669]"}`} />
        <div>
          <p className="font-bold text-[15px]" style={{ fontFamily: displayFont }}>
            {t("You're on the list.", "আপনি তালিকাভুক্ত।")}
          </p>
          <p className={`text-[13px] mt-1 ${dark ? "text-white/70" : "text-[#047857]"}`}>
            {t(
              "We'll send updates in Bangla and English. No spam — unsubscribe any time.",
              "আমরা বাংলা ও ইংরেজিতে আপডেট পাঠাবো। কোনো স্প্যাম নয় — যেকোনো সময় আনসাবস্ক্রাইব করুন।",
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-2xl border p-5 sm:p-6 ${
        dark
          ? "border-white/15 bg-white/[0.04]"
          : "border-[#e2e8f0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      }`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div
          className={`w-8 h-8 rounded-xl flex items-center justify-center ${
            dark
              ? "bg-white/10 text-[#9ce6cd]"
              : "bg-[#e6faf3] text-[#1ba179]"
          }`}
        >
          <Mail className="w-4 h-4" />
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
            dark ? "text-[#9ce6cd]" : "text-[#1ba179]"
          }`}
        >
          <Sparkles className="w-3 h-3 inline mr-1" />
          {t("Newsletter", "নিউজলেটার")}
        </span>
      </div>
      <h3
        className={`text-[18px] sm:text-[20px] font-bold leading-tight ${
          dark ? "text-white" : "text-[#0c2340]"
        }`}
        style={{ fontFamily: displayFont }}
      >
        {t(
          "Grow a modern practice — get monthly tips.",
          "আধুনিক প্র্যাকটিস — মাসিক টিপস পান।",
        )}
      </h3>
      <p
        className={`mt-1.5 text-[13px] leading-relaxed ${
          dark ? "text-white/60" : "text-[#64748b]"
        }`}
      >
        {t(
          "Product updates, clinic playbooks, and BD healthcare insights. One email a month.",
          "প্রোডাক্ট আপডেট, ক্লিনিক প্লেবুক ও বিডি হেলথকেয়ার ইনসাইট। মাসে একটি ইমেইল।",
        )}
      </p>

      <div className={`mt-4 grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-2.5`}>
        {!compact && (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder={t("Your name (optional)", "আপনার নাম (ঐচ্ছিক)")}
            className={`w-full h-11 px-4 rounded-xl text-[14px] outline-none transition ${
              dark
                ? "bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 focus:border-[#9ce6cd]"
                : "bg-white border border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#1ba179]"
            }`}
          />
        )}
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={320}
          placeholder={t("you@clinic.com", "you@clinic.com")}
          className={`w-full h-11 px-4 rounded-xl text-[14px] outline-none transition ${
            dark
              ? "bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 focus:border-[#9ce6cd]"
              : "bg-white border border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#1ba179]"
          }`}
        />
      </div>

      {!compact && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {INTERESTS.map((it) => {
            const active = interest === it.v;
            return (
              <button
                key={it.v}
                type="button"
                onClick={() => setInterest(it.v)}
                className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border transition ${
                  active
                    ? dark
                      ? "bg-[#1ba179] border-[#1ba179] text-white"
                      : "bg-[#0c2340] border-[#0c2340] text-white"
                    : dark
                      ? "bg-transparent border-white/20 text-white/70 hover:border-white/40"
                      : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#1ba179]"
                }`}
              >
                {t(it.en, it.bn)}
              </button>
            );
          })}
        </div>
      )}

      <button
        type="submit"
        disabled={state === "loading"}
        className={`mt-4 w-full h-11 rounded-xl font-bold text-[14px] inline-flex items-center justify-center gap-2 transition disabled:opacity-60 ${
          dark
            ? "bg-white text-[#0c2340] hover:bg-[#e8edf3]"
            : "bg-[#0c2340] text-white hover:bg-[#0f172a]"
        }`}
      >
        {state === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("Subscribing…", "সাবস্ক্রাইব হচ্ছে…")}
          </>
        ) : (
          <>{t("Get the newsletter", "নিউজলেটার নিন")}</>
        )}
      </button>

      {state === "error" && errorMsg && (
        <div
          className={`mt-3 flex items-start gap-2 text-[12.5px] ${
            dark ? "text-[#fca5a5]" : "text-[#b91c1c]"
          }`}
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <p
        className={`mt-3 text-[11px] leading-relaxed ${
          dark ? "text-white/40" : "text-[#94a3b8]"
        }`}
      >
        {t(
          "By subscribing you agree to receive product emails. Unsubscribe any time.",
          "সাবস্ক্রাইব করে আপনি প্রোডাক্ট ইমেইল গ্রহণে সম্মত হচ্ছেন। যেকোনো সময় আনসাবস্ক্রাইব করুন।",
        )}
      </p>
    </form>
  );
}
